class CartNotification extends HTMLElement {
  constructor() {
    super();

    this.notification = document.getElementById('cart-notification');
    this.header = document.querySelector('sticky-header');
    this.onBodyClick = this.handleBodyClick.bind(this);

    this.notification.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelectorAll('button[type="button"]').forEach((closeButton) =>
      closeButton.addEventListener('click', this.close.bind(this))
    );
  }

  open() {
    this.notification.classList.add('animate', 'active');

    this.notification.addEventListener(
      'transitionend',
      () => {
        this.notification.focus();
        trapFocus(this.notification);
      },
      { once: true }
    );

    document.body.addEventListener('click', this.onBodyClick);

    this.dispatchCartViewEvent();
  }

  // The notification's outer element is server-rendered once at page load, so
  // its `cart` Liquid object reflects the pre-add state. The morphed children
  // (inserted from the /cart/add.js sections response in renderContents) are
  // post-add, but they don't expose the full cart shape we need for the event
  // payload. So we keep an explicit /cart.json fetch on open.
  async dispatchCartViewEvent() {
    const { CartViewEvent } = window.StandardEvents || {};
    if (!CartViewEvent) return;

    try {
      const response = await fetch(`${routes.cart_url}.json`);
      const cart = await response.json();
      if (!cart?.currency) return;

      this.dispatchEvent(
        new CartViewEvent({
          context: 'dialog',
          cart: CartViewEvent.createCartFromAjaxResponse(cart),
        })
      );
    } catch (e) {
      // cart:view is informational; swallow fetch errors
    }
  }

  close() {
    this.notification.classList.remove('active');
    document.body.removeEventListener('click', this.onBodyClick);

    removeTrapFocus(this.activeElement);
  }

  renderContents(parsedState) {
    this.cartItemKey = parsedState.key;
    this.resetSummary();
    this.getSectionsToRender().forEach((section) => {
      const html = parsedState.sections && parsedState.sections[section.id];
      if (!html) return;

      /* v568：ヘッダーのカート個数だけは、Dawn の cart-icon-bubble ではなく
         このテーマのヘッダー（aug-header）の中に差し込む。 */
      if (section.id === 'aug-cart-count') {
        this.updateHeaderCount(html);
        return;
      }

      /* v568：受け皿が無いページでも処理が止まらないようにする。
         元の実装は getElementById(...) が null のとき例外で止まっていた。 */
      const target = document.getElementById(section.id);
      if (!target) return;
      const inner = this.getSectionInnerHTML(html, section.selector);
      if (inner === null) return;
      target.innerHTML = inner;

      if (section.id === 'aug-cart-notification-summary') {
        const summary = target.querySelector('[data-aug-notification-summary]');
        if (!summary) return;
        target.hidden = false;
        this.querySelector('[data-aug-summary-fallback]').hidden = true;
        const checkout = this.querySelector('#cart-notification-form [name="checkout"]');
        if (checkout) {
          checkout.disabled = Number(summary.dataset.augSummaryItemCount) < 1 ||
            Number(summary.dataset.augSummaryRestrictedCount) > 0;
        }
        const guard = target.querySelector('[data-aug-cart-guard]');
        if (guard) {
          document.dispatchEvent(new CustomEvent('aug:cart-guard-snapshot', { detail: { guard } }));
        }
      }
    });

    if (this.header) this.header.reveal();
    this.open();
  }

  resetSummary() {
    const target = this.querySelector('#aug-cart-notification-summary');
    if (target) {
      target.replaceChildren();
      target.hidden = true;
    }
    const fallback = this.querySelector('[data-aug-summary-fallback]');
    if (fallback) fallback.hidden = false;
    const checkout = this.querySelector('#cart-notification-form [name="checkout"]');
    if (checkout) checkout.disabled = false;
  }

  /* v568：ヘッダーのカートアイコンの個数バッジを差し替える。
     sections/aug-cart-count.liquid が返すマークアップをそのまま入れ直す。 */
  updateHeaderCount(html) {
    try {
      const link = document.querySelector('a.aug-hd__icon[href$="/cart"]');
      if (!link) return;

      const inner = this.getSectionInnerHTML(html);
      if (inner === null) return;

      const current = link.querySelector('.aug-hd__count');
      if (current) current.remove();

      const fresh = inner.trim();
      if (fresh) link.insertAdjacentHTML('beforeend', fresh);
    } catch (e) {
      // 個数の表示が更新できなくても、カート自体は正しく更新されている
    }
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-notification-product',
        selector: `[id="cart-notification-product-${this.cartItemKey}"]`,
      },
      {
        id: 'aug-cart-notification-summary',
      },
      {
        /* v568：Dawn 標準の cart-icon-bubble はこのテーマのヘッダーでは使っていないため、
           AUG ヘッダー用の受け皿に差し替えている。 */
        id: 'aug-cart-count',
      },
    ];
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    const el = new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
    return el ? el.innerHTML : null;
  }

  handleBodyClick(evt) {
    const target = evt.target;
    if (target !== this.notification && !target.closest('cart-notification')) {
      const disclosure = target.closest('details-disclosure, header-menu');
      this.activeElement = disclosure ? disclosure.querySelector('summary') : null;
      this.close();
    }
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-notification', CartNotification);
